import { useRef, useState } from "react"
import { ScrollableArea } from "@nova/component-scrollable-area"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { GenericInspectorPanelContainer } from "../inspector-panels/GenericInspectorPanel"
import { selectSelectedLayerIds } from "panels/content/side-panel/slice.new"
import { useProjectMapComments } from "features/comments/Comments"
import { selectMapCommentListSort, setListSort } from "plugins/Toolbox/MapComments/CommentSlice"
import { MapCommentListCard } from "./MapCommentListCard"
import { CommentDayHeader } from "./CommentDayHeader"
import { CommentListHeader } from "./CommentListHeader"
import { InsetScrollbar } from "./InsetScrollbar"
import { EVERYONE_CREATOR } from "./MapCommentFilterMenu"
import { groupCommentsByDay } from "./groupCommentsByDay"
import { useFilteredMapComments } from "./useFilteredMapComments"
import { openMapComment } from "./module"

export const MapCommentList = () => {
    const dispatch = useAppDispatch()
    const { comments, isLoading } = useProjectMapComments()
    const selectedLayerIds = useAppSelector(selectSelectedLayerIds)
    const sort = useAppSelector(selectMapCommentListSort)
    const [search, setSearch] = useState("")
    const [creatorId, setCreatorId] = useState(EVERYONE_CREATOR)
    const [headerHeight, setHeaderHeight] = useState(0)
    const scrollRef = useRef<HTMLDivElement>(null)

    const { withMarker, filtered } = useFilteredMapComments(comments, { search, creatorId, sort })
    const showControls = !isLoading && !!comments && withMarker.length > 0

    return (
        <GenericInspectorPanelContainer>
            <div className="relative flex flex-col min-h-0 pb-4">
                <CommentListHeader
                    search={search}
                    onSearchChange={setSearch}
                    creatorId={creatorId}
                    onCreatorChange={setCreatorId}
                    sort={sort}
                    onSortChange={(next) => dispatch(setListSort(next))}
                    showControls={showControls}
                    onHeightChange={setHeaderHeight}
                />
                {isLoading ? (
                    <div
                        className="flex items-center justify-center py-10"
                        style={{ marginTop: headerHeight }}
                    >
                        <div className="loader-sm" />
                    </div>
                ) : comments && withMarker.length === 0 ? (
                    <div className="my-5 px-2" style={{ marginTop: headerHeight }}>
                        <h5 className="text-white text-center m-auto pt-8 pb-4">
                            Click anywhere on the map
                            <br /> to leave a comment.
                        </h5>
                    </div>
                ) : (
                    <>
                        <InsetScrollbar scrollRef={scrollRef} topInset={headerHeight} />
                        <ScrollableArea
                            ref={scrollRef}
                            className="flex flex-col gap-2 pb-2 [&::-webkit-scrollbar]:hidden"
                            style={{ paddingTop: headerHeight, scrollbarWidth: "none" }}
                        >
                            {groupCommentsByDay(filtered).map((section) => (
                                <div key={section.label} className="flex flex-col">
                                    <CommentDayHeader label={section.label} />
                                    {section.items.map((comment) => (
                                        <MapCommentListCard
                                            key={comment.id}
                                            comment={comment}
                                            isActive={selectedLayerIds.includes(comment.entityId)}
                                            onClick={() => openMapComment(comment.entityId)}
                                        />
                                    ))}
                                </div>
                            ))}
                        </ScrollableArea>
                    </>
                )}
            </div>
        </GenericInspectorPanelContainer>
    )
}
